import React from 'react';

import { Form } from 'antd';
import { DatePickerProps } from 'antd/lib/date-picker';
import styled from 'styled-components';

import DatePicker from 'components/lib/DatePicker';
import Input from 'components/lib/Input';
import Select from 'components/lib/Select';
import TextArea from 'components/lib/TextArea';

import CreateEditActionPlansModalOwnerSelect from './CreateEditActionPlansModalOwnerSelect';
import {
  CreateEditActionPlansModalSelect,
  CreateEditActionPlansModalSelectOptionLabel,
} from './CreateEditActionPlansModalSelectStyles';
import { OwnerOption, SelectOption } from './CreateEditActionPlansModalTypes';

const ACTION_PLAN_TITLE_MAX_LENGTH = 255;

const CreateModalLeft = styled.div`
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--line-gray);
  padding: 31px 29px 29px;
  background: var(--white);
  min-height: 0;
  overflow: hidden;
`;

const ModalFormRow = styled.div`
  flex-shrink: 0;
  margin-bottom: 13px;
`;

const DescriptionFormRow = styled(ModalFormRow)`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  margin-bottom: 0;
`;

const Label = styled.div`
  flex-shrink: 0;
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
  margin-bottom: 7px;
`;

const FieldItem = styled(Form.Item)`
  && {
    margin-bottom: 0;
  }
`;

const DescriptionFieldItem = styled(FieldItem)`
  && {
    display: flex;
    flex: 1;
    min-height: 0;
    width: 100%;

    .ant-form-item-row,
    .ant-form-item-control,
    .ant-form-item-control-input,
    .ant-form-item-control-input-content {
      display: flex;
      flex: 1;
      min-height: 0;
      width: 100%;
    }

    .ant-form-item-control-input {
      align-items: stretch;
    }

    .ant-form-item-control-input-content {
      align-items: stretch;
    }
  }
`;

const SidebarInput = styled(Input)`
  &&& {
    height: 37px;
    width: 100%;
  }
`;

const SidebarDatePickerContainer = styled.div`
  .ant-picker {
    width: 100%;
    background: var(--background-gray);
  }
`;

const SidebarDatePicker = (props: DatePickerProps) => (
  <SidebarDatePickerContainer>
    <DatePicker placement="bottomLeft" placeholder="Enter Date" {...props} />
  </SidebarDatePickerContainer>
);

const SidebarTextArea = styled(TextArea)`
  &&& {
    flex: 1;
    height: 100%;
    min-height: 0;
    width: 100%;
    resize: none;
  }
`;

interface Props {
  categoryOptions: SelectOption[];
  ownerOptions: OwnerOption[];
}

const CreateEditActionPlansModalSidebar = ({ categoryOptions, ownerOptions }: Props) => {
  return (
    <CreateModalLeft>
      <ModalFormRow>
        <Label>Title</Label>
        <FieldItem
          name="name"
          rules={[
            { required: true, whitespace: true, message: 'Title is required' },
            { max: ACTION_PLAN_TITLE_MAX_LENGTH, message: 'Title must be 255 characters or fewer' },
          ]}
        >
          <SidebarInput maxLength={ACTION_PLAN_TITLE_MAX_LENGTH} placeholder="Enter Title" />
        </FieldItem>
      </ModalFormRow>

      <ModalFormRow>
        <Label>Category</Label>
        <FieldItem name="focus_area_id" rules={[{ required: true, message: 'Category is required' }]}>
          <CreateEditActionPlansModalSelect
            className="rectangular"
            placeholder="Select"
            allowClear
            optionLabelProp="label"
            labelRender={({ label }) => (
              <CreateEditActionPlansModalSelectOptionLabel>{label}</CreateEditActionPlansModalSelectOptionLabel>
            )}
          >
            {categoryOptions.map((categoryOption) => (
              <Select.Option key={categoryOption.value} value={categoryOption.value} label={categoryOption.label}>
                <CreateEditActionPlansModalSelectOptionLabel>
                  {categoryOption.label}
                </CreateEditActionPlansModalSelectOptionLabel>
              </Select.Option>
            ))}
          </CreateEditActionPlansModalSelect>
        </FieldItem>
      </ModalFormRow>

      <ModalFormRow>
        <Label>Owner</Label>
        <FieldItem name="owner_id">
          <CreateEditActionPlansModalOwnerSelect ownerOptions={ownerOptions} placeholder="Search users" />
        </FieldItem>
      </ModalFormRow>

      <ModalFormRow>
        <Label>Due Date</Label>
        <FieldItem name="due_date">
          <SidebarDatePicker />
        </FieldItem>
      </ModalFormRow>

      <DescriptionFormRow>
        <Label>Description</Label>
        <DescriptionFieldItem name="description" rules={[{ required: true, message: 'Description is required' }]}>
          <SidebarTextArea placeholder="Enter description" />
        </DescriptionFieldItem>
      </DescriptionFormRow>
    </CreateModalLeft>
  );
};

export default CreateEditActionPlansModalSidebar;
